// Asistente de IA (D39, D40, D46): conversacion con el asistente, que usa la cuenta de Claude o de ChatGPT de la persona
// (la que elija) y solo las
// herramientas del CRM (ve lo mismo que ella). Vive en una ventana flotante (ui/AsistenteFlotante.tsx) que acompaña en
// todas las pantallas; si hay un caso abierto lo sabe ("Resume este caso", "Redacta una respuesta") y su borrador se
// puede pasar al cuadro de respuesta. La respuesta llega en vivo. La conversacion se guarda en este navegador, por
// persona y por asistente (las ultimas 40), y sigue la misma sesion mientras no se empiece una nueva.
import { type ClipboardEvent, type DragEvent, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { flujoConSesion } from '../../api/http';
import { useSession } from '../../auth/session';
import { Icon } from '../../lib/icons';
import { separarEstado, TEXTO_ESTADO } from '../../lib/personaje/cara';
import { estadoAmix, ponerAmix, useEstadoAmix } from '../../lib/personaje/estado';
import { type AdjuntoRef, bloquesIA, descargarTexto, filasCsv, tablaACsv } from '../../lib/markdownIA';
import { archivoWord, bajarArchivo, descargarWord, imprimirDocumento, nombreConExt, tituloDeNombre } from '../../lib/documentoXmartek';
import { hayRespuestaAbierta } from '../../lib/insertarEnRespuesta';
import { archivoZammad } from '../../api/casos';
import { archivoAdjunto } from '../../api/correo';
import { readJSON, writeJSON } from '../../lib/storage';
import { Empty, Skeleton } from '../../ui/bits';
import { Amix } from '../../ui/Amix';
import { EnlaceCaso } from '../../ui/EnlaceCaso';
import { type EstadoAsistente, type Proveedor, PROVEEDORES, pdfDeDocx, subirArchivoAmix, USO_MODELO } from '../../api/asistente';
import { ACEPTA_AMIX, achicarImagen, esImagenAmix, MAX_ADJUNTOS_AMIX, motivoRechazo, nombreCaptura } from '../../lib/adjuntosAmix';
import { NOMBRE_IA, useElegirModelo, useElegirProveedor, useEstadoAsistente } from '../../ui/MiAsistente';
import { adjuntarEnRespuesta, usarEnRespuesta } from '../../ui/Respuesta';
import { toast } from '../../ui/toasts';

type Mensaje = { rol: 'yo' | 'ia'; texto: string; herramientas?: string[]; error?: boolean; modelo?: string; archivos?: string[] };
// Un archivo adjuntado en el chat (D89): sube al momento y va con el siguiente mensaje.
type Adjunto = { clave: string; nombre: string; imagen: boolean; id?: string; error?: string };
type Conversacion = { sesionId: string | null; mensajes: Mensaje[] };
// El caso que la persona tiene abierto.
export type ContextoCaso = { casoId: string; numero: number | null; asunto: string; cliente: string } | null;
// El correo que tiene abierto en Bandeja -> Correos (si no hay un caso abierto).
export type ContextoCorreo = { hiloId: string; asunto: string; de: string } | null;

export const EJEMPLOS = ['Ordena mi día', '¿Qué renovaciones vencen en los próximos 30 días?', '¿Qué casos de Soporte esperan respuesta del cliente hace más de 3 días?', 'Resume el caso más urgente que tengo'];
export const ATAJOS_CASO = ['Resume este caso', 'Redacta una respuesta para el cliente', '¿Qué falta para cerrarlo?'];
export const ATAJOS_CORREO = ['Resume este correo', 'Ayúdame a responder este correo', '¿Qué me están pidiendo?'];
// Una conversacion por asistente: la sesion de uno no sirve en el otro. La de Claude conserva la clave de antes.
const clave = (miembroId: string, proveedor: Proveedor) => `consola.asistente.${miembroId}${proveedor === 'claude' ? '' : `.${proveedor}`}`;
const vacia: Conversacion = { sesionId: null, mensajes: [] };

// La ventana que lo contiene (D59): cerrar y agrandar van en la cabecera de Amix.
export type VentanaAmix = { cerrar: () => void; grande: boolean; agrandar?: () => void };

export function PanelAsistente({
  contexto,
  correo = null,
  pedido,
  onPedidoHecho,
  ventana,
}: {
  contexto: ContextoCaso;
  correo?: ContextoCorreo;
  pedido?: string | null;
  onPedidoHecho?: () => void;
  ventana?: VentanaAmix;
}) {
  const { yo } = useSession();
  const amix = useEstadoAmix();
  const cajaTexto = useRef<HTMLTextAreaElement | null>(null);
  const e = useEstadoAsistente();
  const proveedor: Proveedor = e.data?.proveedor ?? 'claude';
  const conectadas = PROVEEDORES.filter((p) => (e.data?.cuentas ? e.data.cuentas[p]?.conectado : p === 'claude' && e.data?.conectado));
  const [conv, setConv] = useState<Conversacion>(() => readJSON<Conversacion>(clave(yo.miembroId ?? 'nadie', proveedor), vacia));
  const [convDe, setConvDe] = useState<Proveedor>(proveedor);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [arrastrando, setArrastrando] = useState(false);
  const elegirArchivo = useRef<HTMLInputElement | null>(null);
  const corte = useRef<AbortController | null>(null);
  const fin = useRef<HTMLDivElement | null>(null);
  const elegir = useElegirModelo();
  const elegirIA = useElegirProveedor();

  // Al cambiar de asistente se muestra su propia conversacion.
  useEffect(() => {
    if (proveedor === convDe) return;
    setConv(readJSON<Conversacion>(clave(yo.miembroId ?? 'nadie', proveedor), vacia));
    setConvDe(proveedor);
  }, [proveedor, convDe, yo.miembroId]);
  useEffect(() => {
    writeJSON(clave(yo.miembroId ?? 'nadie', convDe), { ...conv, mensajes: conv.mensajes.slice(-40) });
  }, [conv, convDe, yo.miembroId]);
  // Si el elegido no esta conectado pero el otro si, se conversa con el otro.
  useEffect(() => {
    if (e.data && !e.data.conectado && conectadas.length) void elegirIA(conectadas[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e.data?.conectado, conectadas.join()]);
  // Con llaves: en Chrome nuevo scrollIntoView devuelve una promesa, y un efecto que la devuelve hace que React intente
  // "limpiarla" como si fuera una funcion (la pantalla fallaba al llegar la respuesta).
  useEffect(() => {
    fin.current?.scrollIntoView({ block: 'end' });
  }, [conv.mensajes]);

  useLayoutEffect(() => {
    const c = cajaTexto.current;
    if (!c) return;
    c.style.height = 'auto';
    c.style.height = `${Math.min(c.scrollHeight + 2, 120)}px`;
    // Sin barra mientras cabe; con barra solo pasado el tope.
    c.style.overflowY = c.scrollHeight + 2 > 120 ? 'auto' : 'hidden';
  }, [texto, e.data?.conectado]);

  // Amix (D52): saluda al abrir, escucha mientras escribes y vuelve al reposo si borras.
  useEffect(() => {
    if (estadoAmix() === 'reposo') ponerAmix('saludando');
  }, []);
  useEffect(() => {
    if (pensando) return;
    const e = estadoAmix();
    if (texto.trim() && (e === 'reposo' || e === 'saludando' || e === 'feliz')) ponerAmix('escuchando');
    else if (!texto.trim() && e === 'escuchando') ponerAmix('reposo');
  }, [texto, pensando]);

  // Adjuntar (clip, pegar o arrastrar): cada archivo sube enseguida; las imagenes grandes se achican antes.
  const agregarArchivos = (lista: File[]) => {
    const libres = MAX_ADJUNTOS_AMIX - adjuntos.length;
    if (lista.length > libres) toast(`Máximo ${MAX_ADJUNTOS_AMIX} archivos por mensaje.`, 'error');
    for (const f of lista.slice(0, Math.max(0, libres))) {
      const motivo = motivoRechazo(f.name, f.size, f.type);
      if (motivo) {
        toast(motivo, 'error');
        continue;
      }
      const clave = `${Date.now()}-${Math.random()}`;
      const imagen = esImagenAmix(f.name, f.type);
      setAdjuntos((xs) => [...xs, { clave, nombre: f.name, imagen }]);
      const cambiar = (c: Partial<Adjunto>) => setAdjuntos((xs) => xs.map((x) => (x.clave === clave ? { ...x, ...c } : x)));
      void (async () => {
        try {
          const { datos, nombre } = imagen ? await achicarImagen(f) : { datos: f as Blob, nombre: f.name };
          const r = await subirArchivoAmix(datos, nombre);
          cambiar({ id: r.id, nombre: r.nombre });
        } catch (err) {
          cambiar({ error: (err as Error).message });
        }
      })();
    }
  };
  const quitarAdjunto = (clave: string) => setAdjuntos((xs) => xs.filter((x) => x.clave !== clave));
  const subiendo = adjuntos.some((a) => !a.id && !a.error);
  const listos = adjuntos.filter((a) => a.id);
  // Una captura pegada llega como "image.png": se renombra con la hora.
  const pegar = (ev: ClipboardEvent) => {
    const archivos = [...ev.clipboardData.files];
    if (!archivos.length) return;
    ev.preventDefault();
    agregarArchivos(archivos.map((f) => (/^image\.(png|jpe?g)$/i.test(f.name) ? new File([f], nombreCaptura(), { type: f.type }) : f)));
  };
  const soltar = (ev: DragEvent) => {
    ev.preventDefault();
    setArrastrando(false);
    if (ev.dataTransfer.files.length) agregarArchivos([...ev.dataTransfer.files]);
  };

  const enviar = async (pregunta: string) => {
    const conArchivos = listos.length > 0;
    const t = pregunta.trim() || (conArchivos ? 'Revisa los archivos adjuntos.' : '');
    if (!t || pensando || subiendo) return;
    const ids = listos.map((a) => a.id!);
    const archivos = listos.map((a) => a.nombre);
    setAdjuntos([]);
    setTexto('');
    setPensando(true);
    ponerAmix('pensando');
    let fallo = false;
    let recibido = '';
    setConv((c) => ({ ...c, mensajes: [...c.mensajes, { rol: 'yo', texto: t, ...(archivos.length ? { archivos } : {}) }, { rol: 'ia', texto: '', herramientas: [] }] }));
    const cambiarUltimo = (f: (m: Mensaje) => Mensaje) => setConv((c) => ({ ...c, mensajes: [...c.mensajes.slice(0, -1), f(c.mensajes[c.mensajes.length - 1])] }));
    corte.current = new AbortController();
    try {
      const caso = contexto ? { numero: contexto.numero, asunto: contexto.asunto, cliente: contexto.cliente } : undefined;
      const visto = !contexto && correo ? { hiloId: correo.hiloId, asunto: correo.asunto, de: correo.de } : undefined;
      const r = await flujoConSesion('/asistente/api/pedir', { mensaje: t, sesionId: conv.sesionId, caso, correo: visto, proveedor, adjuntos: ids }, corte.current.signal);
      if (!r.ok || !r.body) {
        const j = await r.json().catch(() => ({}));
        cambiarUltimo((m) => ({ ...m, texto: j.mensaje ?? 'El asistente no respondió.', error: true }));
        fallo = true;
        return;
      }
      const lector = r.body.getReader();
      const dec = new TextDecoder();
      let resto = '';
      for (;;) {
        const { done, value } = await lector.read();
        if (done) break;
        resto += dec.decode(value, { stream: true });
        const trozos = resto.split('\n\n');
        resto = trozos.pop() ?? '';
        for (const bruto of trozos) {
          const evento = /^event:\s*(.+)$/m.exec(bruto)?.[1]?.trim();
          const crudo = /^data:\s*(.+)$/m.exec(bruto)?.[1];
          if (!evento || !crudo) continue;
          let d: { sesionId?: string; texto?: string; nuevoBloque?: boolean; nombre?: string; detalle?: string; modelo?: string };
          try {
            d = JSON.parse(crudo);
          } catch {
            continue;
          }
          if (evento === 'sesion' && d.sesionId) setConv((c) => ({ ...c, sesionId: d.sesionId! }));
          if (evento === 'sesion' && d.modelo) cambiarUltimo((m) => ({ ...m, modelo: d.modelo }));
          if (evento === 'texto' && d.texto) recibido += d.texto;
          if (evento === 'texto' && d.texto && estadoAmix() !== 'trabajando') ponerAmix('trabajando');
          if (evento === 'herramienta' && d.nombre) ponerAmix('buscando');
          if (evento === 'error') fallo = true;
          if (evento === 'texto' && d.texto) cambiarUltimo((m) => ({ ...m, texto: m.texto + (d.nuevoBloque && m.texto.trim() ? '\n\n' : '') + d.texto }));
          if (evento === 'herramienta' && d.nombre) cambiarUltimo((m) => ({ ...m, herramientas: [...new Set([...(m.herramientas ?? []), d.nombre!])] }));
          if (evento === 'error') cambiarUltimo((m) => ({ ...m, texto: `${m.texto}${m.texto ? '\n\n' : ''}${d.detalle ?? 'El asistente terminó con un error.'}`, error: true }));
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        cambiarUltimo((m) => ({ ...m, texto: (err as Error).message, error: true }));
        fallo = true;
      }
    } finally {
      // La emocion con que cierra: la que eligio el asistente ([[estado:x]]), o feliz / confundido.
      const elegido = separarEstado(recibido).estado;
      if (elegido)
        setConv((c) => {
          const u = c.mensajes[c.mensajes.length - 1];
          return u?.rol === 'ia' ? { ...c, mensajes: [...c.mensajes.slice(0, -1), { ...u, texto: separarEstado(u.texto).texto }] } : c;
        });
      ponerAmix(corte.current?.signal.aborted ? 'reposo' : fallo ? 'confundido' : (elegido ?? 'feliz'));
      setPensando(false);
      corte.current = null;
    }
  };

  // Un pedido que llega de afuera (Mi dia, "Ordenar mi dia"): se envia solo.
  useEffect(() => {
    if (!pedido || !e.data?.conectado || pensando) return;
    onPedidoHecho?.();
    void enviar(pedido);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido, e.data?.conectado]);

  const conMensajes = conv.mensajes.length > 0;
  const nueva = () => (setConv({ sesionId: null, mensajes: [] }), ponerAmix('saludando'), toast('Conversación nueva.'));
  // Lo que esta haciendo ahora (la ultima herramienta), o el estado de Amix.
  const ultima = conv.mensajes[conv.mensajes.length - 1];
  const haciendo = pensando ? (ultima?.herramientas?.at(-1) ? `${ultima.herramientas.at(-1)}…` : 'Pensando…') : TEXTO_ESTADO[amix];
  const cabecera = (
    <header className="amix-h">
      <button type="button" className="amix-cara" onClick={() => ponerAmix('saludando')} aria-label="Saludar a Amix">
        <Amix estado={amix} size={64} mira />
      </button>
      <div className="amix-quien">
        <div className="amix-fila">
          <h3>Amix</h3>
          {e.data?.conectado ? <ModoAmix estado={e.data} proveedor={proveedor} conectadas={conectadas} ocupado={pensando} elegir={elegir} elegirIA={elegirIA} /> : null}
        </div>
        <small className="note amix-haciendo">{haciendo}</small>
      </div>
      <div className="amix-btns">
        {conMensajes ? (
          <button type="button" className="btn sm" onClick={nueva} disabled={pensando}>
            Nueva
          </button>
        ) : null}
        {ventana?.agrandar ? (
          <button type="button" className="btn sm" onClick={ventana.agrandar} aria-pressed={ventana.grande}>
            {ventana.grande ? 'Achicar' : 'Agrandar'}
          </button>
        ) : null}
        {ventana ? (
          <button type="button" className="btn sm" onClick={ventana.cerrar}>
            Cerrar
          </button>
        ) : null}
      </div>
    </header>
  );

  if (e.isLoading)
    return (
      <div className="amix">
        {cabecera}
        <div className="amix-msgs">
          <Skeleton rows={4} />
        </div>
      </div>
    );
  if (!e.data?.conectado)
    return (
      <div className="amix">
        {cabecera}
        <div className="amix-msgs">
          <Empty icon="spark" titulo="Conecta tu cuenta de Claude o de ChatGPT" texto="Amix usa tu propia cuenta de Claude (plan Pro o Max) o de ChatGPT. Se conecta una sola vez.">
            <Link className="btn primary" to="/perfil">
              Ir a Mi perfil → Asistente de IA
            </Link>
          </Empty>
        </div>
      </div>
    );

  // Atajos de lo que tiene abierto: un caso, un correo o nada.
  const deContexto = contexto ? ATAJOS_CASO : correo ? ATAJOS_CORREO : null;
  const atajos = deContexto ?? EJEMPLOS;
  const nombre = (yo.nombre ?? '').split(/\s+/)[0];
  return (
    <div
      className={`amix${arrastrando ? ' amix-soltar' : ''}`}
      onDragOver={(ev) => {
        if (![...ev.dataTransfer.types].includes('Files')) return;
        ev.preventDefault();
        if (!arrastrando) setArrastrando(true);
      }}
      onDragLeave={(ev) => !ev.currentTarget.contains(ev.relatedTarget as Node | null) && setArrastrando(false)}
      onDrop={soltar}
    >
      {cabecera}
      {arrastrando ? (
        <div className="amix-soltar-aviso" aria-hidden>
          <Icon name="paperclip" size={22} /> Suelta aquí para que Amix lo lea
        </div>
      ) : null}
      {contexto ? (
        <div className="amix-ctx" title={contexto.asunto}>
          <Icon name="eye" size={13} /> Viendo #{contexto.numero ?? '—'} · {contexto.cliente || contexto.asunto}
        </div>
      ) : correo ? (
        <div className="amix-ctx" title={correo.asunto}>
          <Icon name="eye" size={13} /> Viendo el correo · {correo.asunto}
        </div>
      ) : null}
      <div className="amix-msgs">
        {!conMensajes ? (
          <div className="amix-sugs">
            <p>
              ¡Hola{nombre && nombre !== 'Puesto' ? `, ${nombre}` : ''}! Soy Amix. Puedo revisar tus casos, clientes, agenda, renovaciones y los archivos de los casos, leer los documentos e imágenes que me adjuntes (clip, pegar o arrastrar) y cotizar con los entrenamientos de AMIX. Veo solo lo que tú puedes ver y no cambio nada en el CRM.
            </p>
            {atajos.map((t) => (
              <button key={t} type="button" className="btn amix-sug" onClick={() => void enviar(t)} disabled={pensando}>
                {t}
              </button>
            ))}
          </div>
        ) : null}
        {conv.mensajes.map((m, i) => {
          const enCurso = pensando && i === conv.mensajes.length - 1;
          return (
            <div key={i} className={`asis-m ${m.rol} ${m.error ? 'error' : ''}`}>
              {m.rol === 'ia' && (m.herramientas?.length || m.modelo) ? (
                <div className="asis-herr">
                  {m.modelo ? <span className="asis-modelo">{m.modelo}</span> : null}
                  {(m.herramientas ?? []).map((h) => (
                    <span key={h} className="amix-tag">
                      {h}
                    </span>
                  ))}
                </div>
              ) : null}
              {m.rol === 'ia' && !m.texto && enCurso ? <span className="note">{haciendo}</span> : <TextoIA texto={separarEstado(m.texto).texto} />}
              {m.archivos?.length ? (
                <div className="asis-archivos">
                  {m.archivos.map((n, j) => (
                    <span key={j} className="amix-tag">
                      <Icon name="paperclip" size={11} /> {n}
                    </span>
                  ))}
                </div>
              ) : null}
              {m.rol === 'ia' && m.texto && !m.error && !enCurso ? (
                <span className="asis-acciones">
                  {deContexto ? (
                    <button type="button" className="linkish asis-usar" onClick={() => usarEnRespuesta(m.texto)}>
                      <Icon name="reply" size={12} /> Usar en la respuesta
                    </button>
                  ) : null}
                  {hayRespuestaAbierta() ? (
                    <button type="button" className="linkish asis-usar" onClick={() => void adjuntarPdf(separarEstado(m.texto).texto)} title="Arma el PDF con formato XmarteK y lo adjunta a la respuesta abierta">
                      <Icon name="paperclip" size={12} /> Adjuntar como PDF
                    </button>
                  ) : null}
                  <button type="button" className="linkish asis-usar" onClick={() => void pdfXmartek(separarEstado(m.texto).texto, `amix-${new Date().toISOString().slice(0, 10)}.pdf`)}>
                    <Icon name="download" size={12} /> Guardar como PDF
                  </button>
                  <button type="button" className="linkish asis-usar" onClick={() => void descargarWord(`amix-${new Date().toISOString().slice(0, 10)}.docx`, separarEstado(m.texto).texto)}>
                    <Icon name="download" size={12} /> Guardar como Word
                  </button>
                </span>
              ) : null}
            </div>
          );
        })}
        {conMensajes && deContexto && !pensando ? (
          <div className="amix-atajos">
            {deContexto.map((t) => (
              <button key={t} type="button" className="amix-tag amix-atajo" onClick={() => void enviar(t)}>
                {t}
              </button>
            ))}
          </div>
        ) : null}
        <div ref={fin} />
      </div>
      {/* Para lectores de pantalla: solo "pensando" y "respondio" (la lista no se anuncia entera). */}
      <div className="sr-only" aria-live="polite">
        {pensando ? 'Amix está pensando…' : conMensajes ? 'Amix respondió.' : ''}
      </div>
      {adjuntos.length ? (
        <div className="amix-adjuntos">
          {adjuntos.map((a) => (
            <span key={a.clave} className={`amix-adj${a.error ? ' error' : a.id ? '' : ' subiendo'}`} title={a.error ?? a.nombre}>
              <Icon name={a.imagen ? 'image' : 'paperclip'} size={12} />
              <span className="amix-adj-n">{a.nombre}</span>
              {a.error ? <small>· no se pudo</small> : !a.id ? <small>· subiendo…</small> : null}
              <button type="button" className="amix-adj-x" onClick={() => quitarAdjunto(a.clave)} aria-label={`Quitar ${a.nombre}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <form className="amix-form" onSubmit={(ev) => (ev.preventDefault(), void enviar(texto))}>
        <input
          ref={elegirArchivo}
          type="file"
          multiple
          accept={ACEPTA_AMIX}
          hidden
          onChange={(ev) => {
            agregarArchivos([...(ev.target.files ?? [])]);
            ev.target.value = '';
          }}
        />
        <button type="button" className="btn amix-clip" onClick={() => elegirArchivo.current?.click()} disabled={pensando} aria-label="Adjuntar archivos o imágenes" title="Adjuntar PDF, Word, Excel, PowerPoint o imágenes (también puedes pegar una captura o arrastrar)">
          <Icon name="paperclip" size={16} />
        </button>
        <textarea
          ref={cajaTexto}
          rows={1}
          value={texto}
          onChange={(ev) => setTexto(ev.target.value)}
          onPaste={pegar}
          onKeyDown={(ev) => ev.key === 'Enter' && !ev.shiftKey && (ev.preventDefault(), void enviar(texto))}
          placeholder={listos.length ? 'Pregúntale algo sobre los archivos (o envía así)…' : 'Escríbele a Amix…'}
          aria-label="Mensaje para el asistente"
        />
        {pensando ? (
          <button type="button" className="btn" onClick={() => corte.current?.abort()}>
            Detener
          </button>
        ) : (
          <button className="btn primary" disabled={subiendo || (!texto.trim() && !listos.length)} title={subiendo ? 'Esperando que suban los archivos…' : undefined}>
            Enviar
          </button>
        )}
      </form>
    </div>
  );
}

// Pildora de modo (D59, como la de Kirby): el modelo elegido y, al tocarla, cada modelo con para que sirve. Si estan
// conectados Claude y ChatGPT, tambien se elige con cual se habla.
const ICONO_MODELO = (id: string) => (/haiku|luna/i.test(id) ? '⚡' : /opus|astra/i.test(id) ? '◆' : /fable/i.test(id) ? '✦' : '●');
const nombreCorto = (nombre: string) => nombre.split(' · ')[0];

function ModoAmix({
  estado,
  proveedor,
  conectadas,
  ocupado,
  elegir,
  elegirIA,
}: {
  estado: EstadoAsistente;
  proveedor: Proveedor;
  conectadas: Proveedor[];
  ocupado: boolean;
  elegir: (modelo: string, proveedor?: Proveedor) => Promise<void>;
  elegirIA: (p: Proveedor) => Promise<void>;
}) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (ev: PointerEvent) => !caja.current?.contains(ev.target as Node) && setAbierto(false);
    const esc = (ev: KeyboardEvent) => ev.key === 'Escape' && (ev.stopPropagation(), setAbierto(false));
    window.addEventListener('pointerdown', fuera);
    window.addEventListener('keydown', esc, true);
    return () => {
      window.removeEventListener('pointerdown', fuera);
      window.removeEventListener('keydown', esc, true);
    };
  }, [abierto]);
  const cuentas: [Proveedor, { modelo: string; modelos: { id: string; nombre: string }[] }][] = conectadas.map((p) => [p, estado.cuentas?.[p] ?? estado]);
  const actual = estado.modelos.find((m) => m.id === estado.modelo);
  const etiqueta = proveedor === 'chatgpt' && estado.modelo === 'predeterminado' ? 'ChatGPT' : nombreCorto(actual?.nombre ?? estado.modelo);
  const varios = conectadas.length > 1;
  return (
    <div className="amix-modo" ref={caja}>
      <button
        type="button"
        className="amix-pildora"
        onClick={() => setAbierto((x) => !x)}
        disabled={ocupado}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={`Modo de Amix: ${etiqueta}${varios ? ` con ${NOMBRE_IA[proveedor]}` : ''}`}
      >
        <span aria-hidden>{ICONO_MODELO(estado.modelo)}</span> {etiqueta}
        {varios && etiqueta !== 'ChatGPT' ? <span className="amix-pildora-ia">· {NOMBRE_IA[proveedor]}</span> : null}
        <span aria-hidden>▾</span>
      </button>
      {abierto ? (
        <div className="amix-menu" role="menu">
          {cuentas.map(([p, c]) => (
            <div key={p} role="group" aria-label={NOMBRE_IA[p]}>
              {varios ? <div className="amix-menu-ia">{NOMBRE_IA[p]}</div> : null}
              {c.modelos.map((m) => {
                const elegido = p === proveedor && m.id === c.modelo;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={elegido}
                    onClick={async () => {
                      setAbierto(false);
                      if (p !== proveedor) await elegirIA(p);
                      if (m.id !== c.modelo) await elegir(m.id, p);
                    }}
                  >
                    <span className="amix-menu-ico" aria-hidden>
                      {ICONO_MODELO(m.id)}
                    </span>
                    <span>
                      <b>{nombreCorto(m.nombre)}</b>
                      <small>{USO_MODELO[m.id] ?? m.nombre}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Texto del asistente: parrafos, listas, **negrita** y numeros de caso (#2026...) que abren el caso.
// La respuesta en bloques (D49): tablas con "Descargar CSV", texto de ancho fijo para los graficos de barras y
// bloques de archivo como tarjeta con "Descargar".
function TextoIA({ texto }: { texto: string }) {
  return (
    <div className="asis-texto">
      {bloquesIA(texto).map((b, i) => {
        if (b.tipo === 'titulo') return <p key={i} className="asis-t">{enLinea(b.texto)}</p>;
        if (b.tipo === 'parrafo') return <p key={i}>{enLinea(b.texto)}</p>;
        if (b.tipo === 'lista') return <ul key={i}>{b.items.map((l, j) => <li key={j}>{enLinea(l)}</li>)}</ul>;
        if (b.tipo === 'codigo') return <pre key={i} className="asis-pre">{b.texto}</pre>;
        if (b.tipo === 'archivo') return <ArchivoIA key={i} nombre={b.nombre} contenido={b.contenido} />;
        if (b.tipo === 'adjunto') return <AdjuntoCaso key={i} nombre={b.nombre} adjunto={b.ref} />;
        return (
          <div key={i} className="asis-tabla">
            <div className="asis-tabla-scroll">
              <table>
                <thead>
                  <tr>{b.encabezado.map((c, j) => <th key={j}>{enLinea(c)}</th>)}</tr>
                </thead>
                <tbody>
                  {b.filas.map((f, j) => (
                    <tr key={j}>{f.map((c, k) => <td key={k}>{enLinea(c)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="linkish asis-csv" onClick={() => descargarTexto(`tabla-${new Date().toISOString().slice(0, 10)}.csv`, tablaACsv(b.encabezado, b.filas))}>
              <Icon name="download" size={12} /> Descargar CSV
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Un adjunto de un caso que el asistente ofrece (D57): se baja con la sesion de la persona, por la misma via que en el
// caso (Zammad o el servicio de correo), asi que solo funciona si ella puede ver ese archivo.
function AdjuntoCaso({ nombre, adjunto }: { nombre: string; adjunto: AdjuntoRef }) {
  const [bajando, setBajando] = useState(false);
  const bajar = async () => {
    setBajando(true);
    try {
      const blob = adjunto.origen === 'zammad' ? await archivoZammad(adjunto.casoId, adjunto.articulo, adjunto.id) : await archivoAdjunto(adjunto.mensajeId, adjunto.parte);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      toast(`No se pudo descargar ${nombre}: ${(e as Error).message}`, 'error');
    } finally {
      setBajando(false);
    }
  };
  return (
    <div className="asis-archivo">
      <Icon name="paperclip" />
      <span className="asis-archivo-n">
        <b>{nombre}</b>
        <span className="sb">Adjunto del caso</span>
      </span>
      <button type="button" className="btn sm primary" onClick={() => void bajar()} disabled={bajando}>
        <Icon name="download" size={12} /> {bajando ? 'Bajando…' : 'Descargar'}
      </button>
    </div>
  );
}

// Archivo que entrega el asistente. Los documentos (.pdf, .docx y los .md o .txt) salen con formato XmarteK (D91, D93):
// el Word se arma aqui; el PDF lo hace el servidor con ese mismo Word (si falla, se abre para imprimir). Con un caso o
// un correo abierto se pueden adjuntar directo a la respuesta.
function ArchivoIA({ nombre, contenido }: { nombre: string; contenido: string }) {
  const [haciendo, setHaciendo] = useState<'pdf' | 'word' | 'adjuntar' | null>(null);
  const kb = Math.max(1, Math.round(new Blob([contenido]).size / 1024));
  const filas = /\.csv$/i.test(nombre) ? filasCsv(contenido) : null;
  const pdf = /\.pdf$/i.test(nombre);
  const word = /\.docx?$/i.test(nombre);
  const documento = pdf || word || /\.(md|txt)$/i.test(nombre);
  const hacer = async (que: 'pdf' | 'word' | 'adjuntar') => {
    setHaciendo(que);
    try {
      if (que === 'word') return await descargarWord(nombre, contenido);
      if (que === 'pdf') {
        const f = await archivoPdf(nombre, contenido);
        return f ? bajarArchivo(f, f.name) : undefined;
      }
      const f = word ? await archivoWord(nombre, contenido) : await archivoPdf(nombre, contenido);
      if (f) adjuntarEnRespuesta(f);
    } catch (e) {
      toast(`No se pudo armar ${nombre}: ${(e as Error).message}`, 'error');
    } finally {
      setHaciendo(null);
    }
  };
  return (
    <div className="asis-archivo">
      <Icon name="paperclip" />
      <span className="asis-archivo-n">
        <b>{nombre}</b>
        <span className="sb">
          {filas != null ? `${filas} fila${filas === 1 ? '' : 's'} · ` : ''}
          {pdf ? 'PDF con formato XmarteK' : word ? 'Word con formato XmarteK' : `${kb} KB`}
        </span>
      </span>
      <button type="button" className="btn sm primary" disabled={!!haciendo} onClick={() => (pdf ? void hacer('pdf') : word ? void hacer('word') : descargarTexto(nombre, contenido))}>
        <Icon name="download" size={12} /> {haciendo === 'pdf' || haciendo === 'word' ? 'Armando…' : pdf ? 'PDF' : word ? 'Word' : 'Descargar'}
      </button>
      {documento && !word ? (
        <button type="button" className="btn sm" disabled={!!haciendo} onClick={() => void hacer('word')}>
          Word
        </button>
      ) : null}
      {documento && !pdf ? (
        <button type="button" className="btn sm" disabled={!!haciendo} onClick={() => void hacer('pdf')}>
          PDF
        </button>
      ) : null}
      {documento && hayRespuestaAbierta() ? (
        <button type="button" className="btn sm" disabled={!!haciendo} onClick={() => void hacer('adjuntar')} title="Lo agrega como adjunto en la respuesta del caso o correo abierto">
          <Icon name="paperclip" size={12} /> {haciendo === 'adjuntar' ? 'Adjuntando…' : 'Adjuntar a la respuesta'}
        </button>
      ) : null}
    </div>
  );
}

// PDF con formato XmarteK: el servidor convierte el Word (asi salen iguales). Si no puede, se abre para imprimir y
// devuelve null.
async function archivoPdf(nombre: string, md: string): Promise<File | null> {
  try {
    const blob = await pdfDeDocx(await archivoWord(nombre, md));
    return new File([blob], nombreConExt(nombre, 'pdf'), { type: 'application/pdf' });
  } catch (e) {
    console.warn('PDF en el servidor:', (e as Error).message);
    if (!imprimirDocumento(md, tituloDeNombre(nombre))) toast('No se pudo hacer el PDF y el navegador bloqueó la ventana para imprimir.', 'error');
    else toast('No se pudo hacer el PDF en el servidor: en la ventana que se abrió, elige «Guardar como PDF».');
    return null;
  }
}

// La respuesta de AMIX como PDF adjunto de la respuesta abierta (D98): asi el cliente la recibe con formato.
async function adjuntarPdf(md: string) {
  toast('Armando el PDF…');
  const f = await archivoPdf(`respuesta-${new Date().toISOString().slice(0, 10)}.pdf`, md);
  if (f) {
    adjuntarEnRespuesta(f);
    toast(`${f.name} quedó adjunto en la respuesta.`);
  }
}

async function pdfXmartek(md: string, nombre: string) {
  const f = await archivoPdf(nombre, md);
  if (f) bajarArchivo(f, f.name);
}

function enLinea(t: string): ReactNode[] {
  return t.split(/(`[^`\n]+`|\*\*[^*]+\*\*|#\d{6,20})/g).map((x, i) => {
    // `texto` (nombres de archivo, codigos): ancho fijo, sin enlaces adentro.
    if (/^`[^`\n]+`$/.test(x)) return <code key={i} className="asis-cod">{x.slice(1, -1)}</code>;
    // La negrita tambien puede traer un numero de caso ("**#2026...**"): se enlaza adentro.
    if (/^\*\*[^*]+\*\*$/.test(x)) return <b key={i}>{enLinea(x.slice(2, -2))}</b>;
    if (/^#\d{6,20}$/.test(x)) return <EnlaceCaso key={i} numero={x.slice(1)} />;
    return x;
  });
}

